import { useState } from "react";
import { sqmToAcres } from "../lib/sessionDetail";
import type { LiveReading } from "../lib/liveFrames";
import {
  bladesOn,
  isValidSoc,
  SOC_UNREADABLE_LABEL,
  telemetryProblems,
  tempReading,
  text,
  toFahrenheit,
  TEMP_HOT_C,
  TEMP_WARM_C,
} from "../lib/telemetryReadings";

/**
 * Everything the robot is reporting for a running session.
 *
 * Problems and the headline readings stay pinned — they answer "is it working,
 * and if not why", which is why anyone opens this. The diagnostic detail has
 * the same field coverage as the web panel but sits behind tabs: shown all at
 * once it is a wall of numbers that buries the four readings that matter, and
 * pushes the map off the screen.
 *
 * Anything the robot didn't report renders as an em dash, never a zero. On a
 * live feed "not reported" and "zero" are different claims, and two firmware
 * quirks make that distinction load-bearing (see `telemetryReadings`): protobuf
 * sends 0 for unpopulated temperatures, and an unreadable battery pack reports
 * a SOC of 300.
 */
export function LiveTelemetryPanel({
  reading,
  frameCount,
  connected,
  stale,
  lastFrameMs,
}: {
  reading: LiveReading | null;
  frameCount: number;
  connected: boolean;
  stale: boolean;
  lastFrameMs: number | null;
}) {
  const [tab, setTab] = useState<TabKey>("position");
  const problems = reading ? telemetryProblems(reading) : [];
  const blade = reading ? bladesOn(reading) : null;

  return (
    <div className="dp" style={{ marginTop: 8 }}>
      <p className="session-video-title">LIVE TELEMETRY</p>

      {reading == null ? (
        <p className="session-video-note">
          {/* "Waiting" and "disconnected" are different situations and need
              telling apart: one is "any moment now", the other is "this panel
              is not going to update". */}
          {connected
            ? "Subscribed — the first reading should arrive shortly."
            : "Live channel disconnected — no readings available."}
        </p>
      ) : (
        <>
          {/* Problems first. The section stays mounted when clear rather than
              collapsing: pause_cause and wheel_slip flap frame to frame, and a
              card that changes height mid-stream shifts everything below it. */}
          <div className={"live-faults" + (problems.length === 0 ? " clear" : "")}>
            {problems.length === 0 ? (
              <span>
                <i className="ti ti-circle-check" aria-hidden="true" /> No faults
                reported
              </span>
            ) : (
              problems.map((p) => (
                <span key={p}>
                  <i className="ti ti-alert-triangle" aria-hidden="true" /> {p}
                </span>
              ))
            )}
          </div>

          <div className="live-readings">
            <Reading
              label="BATTERY"
              value={
                isValidSoc(reading.battery_charge_percent)
                  ? String(Math.round(reading.battery_charge_percent))
                  : SOC_UNREADABLE_LABEL
              }
              unit="%"
              sub={num(reading.battery_voltage, " V", 1)}
              color={batteryColor(reading.battery_charge_percent)}
            />
            <Reading
              label="BLADE"
              value={blade == null ? "—" : blade ? "On" : "Off"}
              sub={num(reading.blade_rpm, " rpm")}
              color={blade ? "#22c55e" : undefined}
            />
            <Reading
              label="SPEED"
              value={num(reading.zlac_velocity_x, "", 2)}
              unit=" m/s"
              sub={`cmd ${num(reading.cmd_velocity_x, "", 2)}`}
            />
            <Reading
              label="MODE"
              value={
                reading.is_autonomous == null
                  ? "—"
                  : reading.is_autonomous
                  ? "Auto"
                  : "Manual"
              }
              sub={text(reading.planar_state) || "—"}
              color={reading.is_autonomous === false ? "#f59e0b" : undefined}
            />
          </div>

          <div className="live-tabs" role="tablist">
            {TABS.map((t) => (
              <button
                key={t.key}
                role="tab"
                aria-selected={tab === t.key}
                className={"live-tab" + (tab === t.key ? " active" : "")}
                onClick={() => setTab(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="live-tab-panel" role="tabpanel">
            {tab === "position" && (
              <>
                <Row label="GPS accuracy" value={num(reading.gps_h_accuracy, " m", 2)} color={gpsColor(reading.gps_h_accuracy)} />
                <Row label="GPS HDOP" value={num(reading.gps_hdop, "", 2)} />
                <Row label="GPS status" value={text(reading.gps_status) || "—"} />
                <Row label="Steps left" value={num(reading.num_steps_left)} />
              </>
            )}

            {tab === "motion" && (
              <>
                <Row label="Cmd velocity" value={`${num(reading.cmd_velocity_x, "", 2)} m/s · ${num(reading.cmd_velocity_theta, "", 2)} rad/s`} />
                <Row label="Measured velocity" value={`${num(reading.zlac_velocity_x, "", 2)} m/s · ${num(reading.zlac_velocity_theta, "", 2)} rad/s`} />
                <Row
                  label="Wheel slip"
                  value={reading.wheel_slip_detected == null ? "—" : reading.wheel_slip_detected ? "Detected" : "No"}
                  color={reading.wheel_slip_detected ? "#ef4444" : undefined}
                />
              </>
            )}

            {tab === "power" && (
              <>
                <Row label="Bus voltage" value={num(reading.bus_voltage, " V", 1)} />
                <Row label="Battery voltage" value={num(reading.battery_voltage, " V", 1)} />
                <Row label="Current draw" value={num(reading.current_consumed, " A", 1)} />
                <Row label="Motor current L / R" value={`${num(reading.left_motor_current, " A", 1)} · ${num(reading.right_motor_current, " A", 1)}`} />
              </>
            )}

            {tab === "temps" && (
              <div className="live-temps">
                <Temp label="CPU" c={reading.cpu_temperature} />
                <Temp label="Driver" c={reading.driver_temperature} />
                <Temp label="Blade" c={reading.blade_motor_temperature} />
                <Temp label="Motor L" c={reading.left_motor_temperature} />
                <Temp label="Motor R" c={reading.right_motor_temperature} />
              </div>
            )}

            {tab === "progress" && (
              <>
                <Row
                  label="Mowed area"
                  value={
                    reading.mowed_area == null
                      ? "—"
                      : `${sqmToAcres(reading.mowed_area).toFixed(3)} ac`
                  }
                />
                <Row label="Mowing time" value={durationLabel(reading.mowed_duration)} />
                <Row label="Certainty" value={num(reading.certainty_value, "", 2)} />
              </>
            )}
          </div>

          <p className="session-video-meta">
            {frameCount} position{frameCount === 1 ? "" : "s"} plotted
            {lastFrameMs != null &&
              ` · updated ${new Date(lastFrameMs).toLocaleTimeString()}`}
            {stale && " · no new frames in the last 30s"}
            {!connected && " · live channel disconnected"}
          </p>
        </>
      )}
    </div>
  );
}

type TabKey = "position" | "motion" | "power" | "temps" | "progress";

/** Ordered by how often you'd reach for them from a management view: where it
 *  is and how well it's fixed, then whether it's actually moving, then the
 *  electrical and thermal detail you only check when something looks wrong. */
const TABS: { key: TabKey; label: string }[] = [
  { key: "position", label: "Position" },
  { key: "motion", label: "Motion" },
  { key: "power", label: "Power" },
  { key: "temps", label: "Temps" },
  { key: "progress", label: "Progress" },
];

function Row({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color?: string;
}) {
  return (
    <div className="live-row">
      <span>{label}</span>
      <span style={{ color: color ?? "var(--color-text-primary)" }}>{value}</span>
    </div>
  );
}

function Reading({
  label,
  value,
  unit,
  sub,
  color,
}: {
  label: string;
  value: string;
  unit?: string;
  sub?: string;
  color?: string;
}) {
  return (
    <div className="live-reading">
      <p className="kl">{label}</p>
      <p className="kv" style={{ fontSize: value.length > 6 ? 12 : 16, color }}>
        {value}
        {/* Only pin a unit to a real number — "Disconnected %" reads as a
            measurement. Test for a number rather than listing the sentinels. */}
        {unit && /^-?[\d.]/.test(value) && (
          <span style={{ fontSize: 11 }}>{unit}</span>
        )}
      </p>
      {sub && <p className="live-reading-sub">{sub}</p>}
    </div>
  );
}

/** One temperature cell, tinted once it passes the warm/hot thresholds. The
 *  thresholds are Celsius and compare the raw reading; only the display is °F. */
function Temp({ label, c }: { label: string; c: number | null | undefined }) {
  const r = tempReading(c);
  const color =
    r == null ? undefined : r >= TEMP_HOT_C ? "#ef4444" : r >= TEMP_WARM_C ? "#f59e0b" : undefined;
  return (
    <div className="live-temp">
      <p className="kl">{label}</p>
      <p className="live-temp-val" style={{ color }}>
        {r == null ? "—" : Math.round(toFahrenheit(r))}
      </p>
    </div>
  );
}

/** A reading with an optional unit, or an em dash when it wasn't reported. */
function num(
  n: number | null | undefined,
  unit = "",
  digits = 0,
): string {
  return typeof n === "number" ? `${n.toFixed(digits)}${unit}` : "—";
}

function durationLabel(seconds: number | null | undefined): string {
  if (typeof seconds !== "number") return "—";
  if (seconds < 60) return `${Math.round(seconds)} sec`;
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

function batteryColor(pct: number | null | undefined): string | undefined {
  if (!isValidSoc(pct)) return undefined;
  if (pct <= 20) return "#ef4444";
  if (pct <= 40) return "#f59e0b";
  return undefined;
}

/** Horizontal accuracy in metres; RTK-grade is centimetres, so past a metre the
 *  position drawn on the map is not to be trusted. */
function gpsColor(acc: number | null | undefined): string | undefined {
  if (typeof acc !== "number") return undefined;
  if (acc >= 1) return "#ef4444";
  if (acc >= 0.5) return "#f59e0b";
  return undefined;
}
